import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { lcTimeslotApi, lcEnrollmentApi, Timeslot, RosterStudent } from '@/api/lc'
import { freeTrialApi } from '@/api/freeTrials'
import { learnerStatusApi } from '@/api/learnerStatus'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@quebon/ui/components/dialog'
import { AttendanceHistoryModal } from '@/components/admin/members/AttendanceHistoryModal'
import { LearnerConsultationModal } from '@/components/admin/members/LearnerConsultationModal'
import { LeaveModal, WithdrawModal } from '@/components/admin/members/LearnerStatusModals'
import { ContentFeePurchaseModal } from '@/components/admin/contentFee/ContentFeePurchaseModal'
import { CourseChangeModal } from '@/components/admin/members/CourseChangeModal'
import { TrialSetChangeModal } from '@/components/admin/members/TrialSetChangeModal'
import { RowActionMenu } from '@/components/admin/members/RowActionMenu'
import type { RowActionItem } from '@/components/admin/members/RowActionMenu'
import { useTextbookAlerts } from '@/hooks/admin/useTextbookRequests'
import { contentPaymentBadge } from '@/utils/contentFeeGate'
import { getGradeLevelLabel } from '@/utils/studentLabels'
import { toast } from 'sonner'
import { useAuthStore } from '@/store/authStore'
import { hasLcPermission } from '@/utils/roleUtils'

interface Props {
  timeslot?: Timeslot
  students?: RosterStudent[]
  showClassColumn?: boolean
  showHeader?: boolean
  onNavigateAway?: () => void
  onDataInvalidated?: () => void
  maxHeightClassName?: string
  trialEnabled?: boolean
}

const DASH = '—'

const STATUS_BADGE: Record<string, string> = {
  ENROLLED: 'bg-green-100 text-green-700',
  ON_LEAVE: 'bg-amber-100 text-amber-700',
  WAITING: 'bg-gray-100 text-gray-600',
  WITHDRAWN: 'bg-red-100 text-red-600',
}

type RosterFilter = 'ALL' | 'ENROLLED' | 'ON_LEAVE' | 'WITHDRAWN'
const FILTERS: RosterFilter[] = ['ALL', 'ENROLLED', 'ON_LEAVE']

type ActionType = 'move' | 'remove'
const ACTION_ENTRIES: { key: ActionType; testid: string }[] = [
  { key: 'move', testid: 'roster-move' },
  { key: 'remove', testid: 'roster-remove' },
]

export default function ClassRosterPanel({
  timeslot,
  students: externalStudents,
  showClassColumn = false,
  showHeader = true,
  onNavigateAway,
  onDataInvalidated,
  maxHeightClassName = 'max-h-[28rem]',
  trialEnabled = false,
}: Props) {
  const { t } = useTranslation(['admin', 'common'])
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const user = useAuthStore((state) => state.user)
  const canChangeCourse = hasLcPermission(user ?? undefined, 'CLASS')
  const canViewConsultation = hasLcPermission(user ?? undefined, 'STUDENT')
  const [statusFilter, setStatusFilter] = useState<RosterFilter>('ALL')
  const [action, setAction] = useState<{ type: ActionType; student: RosterStudent; timeslotId: number } | null>(null)
  const [leaveFor, setLeaveFor] = useState<RosterStudent | null>(null)
  const [withdrawFor, setWithdrawFor] = useState<RosterStudent | null>(null)
  const [attendanceFor, setAttendanceFor] = useState<RosterStudent | null>(null)
  const [consultationFor, setConsultationFor] = useState<RosterStudent | null>(null)
  const [contentFeeTarget, setContentFeeTarget] = useState<{ userId: number; statusLabel: string } | null>(null)
  const [courseChangeFor, setCourseChangeFor] = useState<RosterStudent | null>(null)
  const [trialSetChangeFor, setTrialSetChangeFor] = useState<RosterStudent | null>(null)

  const { data: rosterData } = useQuery({
    queryKey: ['lcRoster', timeslot?.timeslotId ?? 0],
    queryFn: () => lcTimeslotApi.getRoster(timeslot!.timeslotId),
    enabled: externalStudents == null && timeslot != null,
  })
  const students = externalStudents ?? rosterData?.data?.data ?? []
  const showTrialColumn = trialEnabled || students.some((student) => student.trialId != null)
  const filteredStudents = externalStudents != null || statusFilter === 'ALL'
    ? students
    : students.filter((student) => student.status === statusFilter)

  const counts = useMemo(() => {
    const next: Record<RosterFilter, number> = { ALL: students.length, ENROLLED: 0, ON_LEAVE: 0, WITHDRAWN: 0 }
    students.forEach((student) => {
      if (student.status in next) next[student.status as RosterFilter] += 1
    })
    return next
  }, [students])

  const { data: textbookAlerts } = useTextbookAlerts()
  const textbookAlertMap = useMemo(
    () => new Map((textbookAlerts ?? []).map((a) => [a.userId, a.alertCount])),
    [textbookAlerts],
  )

  const genderLabel = (gender: string | null): string =>
    gender ? t(`assignment.gender.${gender}`, { defaultValue: gender }) : DASH

  const statusLabel = (status: string): string =>
    t(`students.memberStatus.${status}`, { defaultValue: status })

  const productWithCompletion = (student: RosterStudent): string => {
    if (!student.productName) return DASH
    if (student.completionRate == null) return student.productName
    return t('roster.productWithCompletion', {
      product: student.productName,
      rate: student.completionRate,
    })
  }

  const goDetail = (userId: number) => {
    onNavigateAway?.()
    navigate(`/admin/students/${userId}`)
  }

  const openAction = (type: ActionType, student: RosterStudent, timeslotId: number) => {
    setAction({ type, student, timeslotId })
  }

  const apiErrorMessage = (err: unknown, fallback: string): string => {
    const code = (err as { response?: { data?: { error?: { code?: string; message?: string } } } })?.response?.data?.error?.code
    const message = (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message
    if (code) return t(`apiError.${code}`, { ns: 'common', defaultValue: message || fallback })
    return message || fallback
  }

  const invalidateStatus = () => {
    queryClient.invalidateQueries({ queryKey: ['lcRoster'] })
    queryClient.invalidateQueries({ queryKey: ['lcRosterSearch'] })
    queryClient.invalidateQueries({ queryKey: ['lc', 'students'] })
    queryClient.invalidateQueries({ queryKey: ['lc', 'learnerStatuses'] })
    queryClient.invalidateQueries({ queryKey: ['lc', 'learnerStatusSummary'] })
    queryClient.invalidateQueries({ queryKey: ['lcEnrollments'] })
    onDataInvalidated?.()
  }

  const leaveMutation = useMutation({
    mutationFn: ({ userId, body }: { userId: number; body: { leaveStart: string; leaveEnd?: string; reason?: string } }) =>
      learnerStatusApi.startLeave(userId, body),
    onSuccess: () => {
      invalidateStatus()
      toast.success(t('students.statusActions.leaveSuccess'))
      setLeaveFor(null)
    },
    onError: (err) => toast.error(apiErrorMessage(err, t('students.statusActions.leaveFailed'))),
  })

  const returnMutation = useMutation({
    mutationFn: (userId: number) => learnerStatusApi.returnFromLeave(userId),
    onSuccess: () => {
      invalidateStatus()
      toast.success(t('students.statusActions.returnSuccess'))
    },
    onError: (err) => toast.error(apiErrorMessage(err, t('students.statusActions.returnFailed'))),
  })

  const withdrawMutation = useMutation({
    mutationFn: ({ userId, body }: { userId: number; body: { reason?: string } }) =>
      learnerStatusApi.withdraw(userId, body),
    onSuccess: () => {
      invalidateStatus()
      toast.success(t('students.statusActions.withdrawSuccess'))
      setWithdrawFor(null)
    },
    onError: (err) => toast.error(apiErrorMessage(err, t('students.statusActions.withdrawFailed'))),
  })

  const revokeTrialMutation = useMutation({
    mutationFn: ({ id, reason }: { id: number; reason: string }) => freeTrialApi.revoke(id, reason),
    onSuccess: (res) => {
      // 취소는 퇴원이 아니다 — 원생 목록·예비 탭·배정 후보·자격까지 함께 갱신한다(spec §10-3, 엣지11)
      invalidateStatus()
      queryClient.invalidateQueries({ queryKey: ['lcFreeTrials'] })
      queryClient.invalidateQueries({ queryKey: ['lcAssignable'] })
      queryClient.invalidateQueries({ queryKey: ['lcFreeTrialEligibility'] })
      queryClient.invalidateQueries({ queryKey: ['lcFreeTrialHistory'] })
      const view = res.data?.data
      if (view?.learnerStatus === 'WAITING') {
        toast.success(
          view.consumed
            ? t('roster.trial.revokeWaitingConsumed')
            : t('roster.trial.revokeWaitingUnused'),
        )
      } else {
        toast.success(t('roster.trial.revokeSuccess'))
      }
    },
    onError: (err) => toast.error(apiErrorMessage(err, t('roster.trial.revokeFailed'))),
  })

  const trialLabel = (student: RosterStudent): string => {
    if (!student.trialStatus) return DASH
    if (student.trialStatus === 'PENDING') return t('roster.trial.status.PENDING', { set: student.trialSetCode })
    if (student.trialStatus === 'ACTIVE') {
      return t('roster.trial.status.ACTIVE', { set: student.trialSetCode, days: student.trialRemainingDays ?? 0 })
    }
    return t(`roster.trial.status.${student.trialStatus}`, { set: student.trialSetCode })
  }

  return (
    <>
      <div className="space-y-3">
        {showHeader && timeslot ? <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-base font-semibold text-gray-900">
              {t('roster.title', { name: timeslot.name })}
            </h2>
            <p className="text-sm text-gray-500">{t('roster.count', { n: filteredStudents.length })}</p>
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-600">
            {t('roster.filter.label')}
            <select
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value as RosterFilter)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              {FILTERS.map((status) => (
                <option key={status} value={status}>
                  {t(`roster.filter.${status}`, { count: counts[status] })}
                </option>
              ))}
            </select>
          </label>
        </div> : null}

        <div className={`${maxHeightClassName} overflow-auto rounded-lg border border-gray-200 bg-white`}>
          {filteredStudents.length === 0 ? (
            <p className="py-8 text-center text-sm text-gray-400">{t('roster.empty')}</p>
          ) : (
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  {([
                    'name',
                    'handle',
                    ...(showClassColumn ? ['timeslotName'] as const : []),
                    'gradeLevel',
                    'gender',
                    'guardianPhone',
                    ...(showTrialColumn ? ['trial'] as const : []),
                    'productName',
                    'currentCurriculum',
                    'status',
                    'unusedLessons',
                    'endDate',
                  ] as const).map((c) => (
                    <th key={c} className="px-3 py-2 text-left text-xs font-medium text-gray-500 whitespace-nowrap">
                      {t(`roster.table.${c}`)}
                    </th>
                  ))}
                  <th className="px-3 py-2" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredStudents.map((s) => {
                  const sourceTimeslotId = s.timeslotId ?? timeslot?.timeslotId
                  const paymentBadge = s.status === 'ENROLLED'
                    ? contentPaymentBadge(s.contentFeeGate, s.contentFeeNotice)
                    : null
                  const paymentRequired = paymentBadge !== null
                  const rowItems: RowActionItem[] = [
                    {
                      key: 'contentPayment',
                      label: t('roster.manage.contentPayment'),
                      testid: `roster-content-payment-${s.userId}`,
                      onClick: () => setContentFeeTarget({
                        userId: s.userId,
                        statusLabel: statusLabel(s.status),
                      }),
                    },
                    {
                      key: 'textbook',
                      label: t('roster.manage.textbook'),
                      testid: `roster-textbook-${s.userId}`,
                      onClick: () => {
                        onNavigateAway?.()
                        navigate(`/admin/students/${s.userId}?tab=textbook`)
                      },
                    },
                    {
                      key: 'attendance',
                      label: t('students.attendanceHistory.action'),
                      testid: `roster-attendance-${s.userId}`,
                      onClick: () => setAttendanceFor(s),
                    },
                    ...(canViewConsultation ? [{
                      key: 'consultation',
                      label: t('students.rowMenu.consultation'),
                      testid: `roster-consultation-${s.userId}`,
                      onClick: () => setConsultationFor(s),
                    }] : []),
                    ...(sourceTimeslotId == null ? [] : ACTION_ENTRIES.map<RowActionItem>((e) => ({
                      key: e.key,
                      label: t(`roster.manage.${e.key}`),
                      testid: `${e.testid}-${s.userId}`,
                      onClick: () => openAction(e.key, s, sourceTimeslotId),
                    }))),
                  ]
                  {/* 무료체험 적용(부여)은 예비 원생 배정(AssignmentModal) 전용 — roster에는 두지 않는다(spec §10-2, 엣지2) */}
                  if (s.trialStatus === 'PENDING' || s.trialStatus === 'ACTIVE') {
                    rowItems.unshift({
                        key: 'trialRevoke',
                        label: t('roster.manage.trialRevoke'),
                        testid: `roster-trial-revoke-${s.userId}`,
                        danger: true,
                        onClick: () => {
                          const reason = window.prompt(t('roster.trial.revokePrompt'))?.trim()
                          if (reason) revokeTrialMutation.mutate({ id: s.trialId!, reason })
                        },
                      })
                  }
                  if (s.status !== 'ON_LEAVE' && s.status !== 'WITHDRAWN') {
                    rowItems.push({
                      key: 'leave',
                      label: t('students.rowMenu.leave'),
                      testid: `roster-leave-${s.userId}`,
                      onClick: () => setLeaveFor(s),
                    })
                  }
                  if (s.status === 'ON_LEAVE') {
                    rowItems.push({
                      key: 'return',
                      label: t('students.rowMenu.return'),
                      testid: `roster-return-${s.userId}`,
                      onClick: () => {
                        if (confirm(t('students.statusActions.returnConfirm', { name: s.fullName }))) {
                          returnMutation.mutate(s.userId)
                        }
                      },
                    })
                  }
                  if (s.status !== 'WITHDRAWN') {
                    rowItems.push({
                      key: 'withdraw',
                      label: t('students.rowMenu.withdraw'),
                      testid: `roster-withdraw-${s.userId}`,
                      danger: true,
                      onClick: () => setWithdrawFor(s),
                    })
                  }
                  const hasCurrentTrial = s.trialStatus === 'PENDING' || s.trialStatus === 'ACTIVE'
                  if (canChangeCourse && s.trialStatus === 'PENDING' && s.trialId != null && s.trialSetCode != null) {
                    rowItems.push({
                      key: 'changeTrialSet',
                      label: t('roster.manage.changeTrialSet'),
                      testid: `roster-change-trial-set-${s.userId}`,
                      onClick: () => setTrialSetChangeFor(s),
                    })
                  } else if (canChangeCourse && !hasCurrentTrial) {
                    rowItems.push({
                      key: 'changeCourse',
                      label: t('roster.manage.changeCourse'),
                      testid: `roster-change-course-${s.userId}`,
                      onClick: () => setCourseChangeFor(s),
                      disabled: !['ENROLLED', 'ON_LEAVE'].includes(s.status),
                    })
                  }

                  return (
                    <tr key={s.userId} data-testid={`roster-row-${s.userId}`} className="hover:bg-gray-50">
                      <td className="px-3 py-2 font-medium whitespace-nowrap">
                        <button
                          type="button"
                          onClick={() => goDetail(s.userId)}
                          className="text-left text-gray-900 hover:text-primary-700 hover:underline"
                        >
                          {s.fullName}
                        </button>
                        {paymentRequired ? (
                          <button
                            type="button"
                            onClick={() => setContentFeeTarget({
                              userId: s.userId,
                              statusLabel: statusLabel(s.status),
                            })}
                            className="ml-1 rounded border border-amber-300 bg-amber-50 px-1.5 py-0.5 text-xs font-semibold text-amber-800 hover:bg-amber-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-amber-500"
                          >
                            {t(paymentBadge === 'AVAILABLE' ? 'students.list.contentPaymentAvailable' : 'students.list.contentPaymentRequired')}
                          </button>
                        ) : (textbookAlertMap.get(s.userId) ?? 0) > 0 ? (
                          <span className="ml-1 rounded bg-amber-100 px-1.5 py-0.5 text-xs font-normal text-amber-700">
                            {t('students.list.textbookAlert')}
                          </span>
                        ) : null}
                      </td>
                      <td className="px-3 py-2 text-gray-600">{s.handle || DASH}</td>
                      {showClassColumn ? (
                        <td className="px-3 py-2 text-gray-600 whitespace-nowrap">{s.timeslotName || DASH}</td>
                      ) : null}
                      <td className="px-3 py-2 text-gray-600">{getGradeLevelLabel(s.gradeLevel, t)}</td>
                      <td className="px-3 py-2 text-gray-600">{genderLabel(s.gender)}</td>
                      <td className="px-3 py-2 text-gray-600 whitespace-nowrap">{s.guardianPhone || DASH}</td>
                      {showTrialColumn ? (
                        <td className="px-3 py-2 whitespace-nowrap">
                          {s.trialStatus ? (
                            <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-700">
                              {trialLabel(s)}
                            </span>
                          ) : DASH}
                        </td>
                      ) : null}
                      <td className="px-3 py-2 text-gray-600 whitespace-nowrap">{productWithCompletion(s)}</td>
                      <td className="px-3 py-2 text-gray-600 whitespace-nowrap">{s.currentCurriculum || DASH}</td>
                      <td className="px-3 py-2">
                        <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${STATUS_BADGE[s.status] ?? 'bg-gray-100 text-gray-600'}`}>
                          {statusLabel(s.status)}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-gray-600 whitespace-nowrap">
                        {s.unusedLessons ?? DASH}
                      </td>
                      <td className="px-3 py-2 text-gray-600 whitespace-nowrap">
                        {s.learningEndDate || s.endDate || t('roster.endDateUnlimited')}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <RowActionMenu
                          triggerLabel={t('roster.manage.label')}
                          triggerTestId={`roster-manage-${s.userId}`}
                          items={rowItems}
                        />
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {action && (
        <RosterActionDialog
          type={action.type}
          student={action.student}
          timeslotId={action.timeslotId}
          onClose={() => setAction(null)}
        />
      )}
      <LeaveModal
        open={!!leaveFor}
        learnerName={leaveFor?.fullName ?? ''}
        pending={leaveMutation.isPending}
        onConfirm={(body) => { if (leaveFor) leaveMutation.mutate({ userId: leaveFor.userId, body }) }}
        onClose={() => setLeaveFor(null)}
      />
      <WithdrawModal
        open={!!withdrawFor}
        learnerName={withdrawFor?.fullName ?? ''}
        pending={withdrawMutation.isPending}
        onConfirm={(body) => { if (withdrawFor) withdrawMutation.mutate({ userId: withdrawFor.userId, body }) }}
        onClose={() => setWithdrawFor(null)}
      />
      {attendanceFor && (
        <AttendanceHistoryModal
          userId={attendanceFor.userId}
          learnerName={attendanceFor.fullName}
          onClose={() => setAttendanceFor(null)}
        />
      )}
      {consultationFor && (
        <LearnerConsultationModal
          userId={consultationFor.userId}
          learnerName={consultationFor.fullName}
          onClose={() => setConsultationFor(null)}
        />
      )}
      {contentFeeTarget !== null && (
        <ContentFeePurchaseModal
          userId={contentFeeTarget.userId}
          learnerStatusLabel={contentFeeTarget.statusLabel}
          open
          onClose={() => {
            setContentFeeTarget(null)
            invalidateStatus()
          }}
        />
      )}
      {courseChangeFor !== null && (courseChangeFor.timeslotId ?? timeslot?.timeslotId) != null && (
        <CourseChangeModal
          learner={{
            userId: courseChangeFor.userId,
            name: courseChangeFor.fullName,
            lcEnrollmentId: courseChangeFor.lcEnrollmentId,
            productName: courseChangeFor.productName,
            timeslotId: (courseChangeFor.timeslotId ?? timeslot?.timeslotId)!,
          }}
          onClose={() => setCourseChangeFor(null)}
          onRequestPayment={(userId: number) => {
            const label = statusLabel(courseChangeFor.status)
            setCourseChangeFor(null)
            setContentFeeTarget({ userId, statusLabel: label })
          }}
        />
      )}
      {trialSetChangeFor?.trialId != null && trialSetChangeFor.trialSetCode != null && (
        <TrialSetChangeModal
          learner={{
            trialId: trialSetChangeFor.trialId,
            userId: trialSetChangeFor.userId,
            name: trialSetChangeFor.fullName,
            currentSet: trialSetChangeFor.trialSetCode,
          }}
          onClose={() => setTrialSetChangeFor(null)}
        />
      )}
    </>
  )
}

interface ActionDialogProps {
  type: ActionType
  student: RosterStudent
  timeslotId: number
  onClose: () => void
}

function RosterActionDialog({ type, student, timeslotId, onClose }: ActionDialogProps) {
  const { t } = useTranslation(['admin', 'common'])
  const queryClient = useQueryClient()
  const [targetTimeslotId, setTargetTimeslotId] = useState<number | undefined>(undefined)

  const apiErrorMessage = (err: unknown, fallback: string): string => {
    const code = (err as { response?: { data?: { error?: { code?: string } } } })?.response?.data?.error?.code
    return code ? t(`apiError.${code}`, { ns: 'common', defaultValue: fallback }) : fallback
  }

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['lcRoster', timeslotId] })
    queryClient.invalidateQueries({ queryKey: ['lcRosterSearch'] })
    queryClient.invalidateQueries({ queryKey: ['lcTimeslots'] })
    queryClient.invalidateQueries({ queryKey: ['lcEnrollments'] })
    queryClient.invalidateQueries({ queryKey: ['lc', 'learnerStatuses'] })
  }

  const { data: timeslotsData } = useQuery({
    queryKey: ['lcTimeslots'],
    queryFn: () => lcTimeslotApi.getAll(false),
    enabled: type === 'move',
  })
  const moveTargets = (timeslotsData?.data?.data || []).filter((ts) => ts.timeslotId !== timeslotId)

  const moveMutation = useMutation({
    mutationFn: (toId: number) => lcEnrollmentApi.update(student.lcEnrollmentId, { timeslotId: toId }),
    onSuccess: () => { invalidate(); toast.success(t('roster.action.toast.moveSuccess')); onClose() },
    onError: (err) => toast.error(apiErrorMessage(err, t('roster.action.toast.moveFailed'))),
  })

  const removeMutation = useMutation({
    mutationFn: () => lcEnrollmentApi.update(student.lcEnrollmentId, { clearTimeslot: true }),
    onSuccess: () => { invalidate(); toast.success(t('roster.action.toast.removeSuccess')); onClose() },
    onError: (err) => toast.error(apiErrorMessage(err, t('roster.action.toast.removeFailed'))),
  })

  const pending = moveMutation.isPending || removeMutation.isPending

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose() }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t(`roster.action.${type}.title`, { name: student.fullName })}</DialogTitle>
        </DialogHeader>

        {type === 'move' && (
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">{t('roster.action.move.targetLabel')}</label>
            <select
              data-testid="roster-move-target"
              value={targetTimeslotId ?? ''}
              onChange={(e) => setTargetTimeslotId(e.target.value ? Number(e.target.value) : undefined)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            >
              <option value="">{t('roster.action.move.targetPlaceholder')}</option>
              {moveTargets.map((ts) => (
                <option key={ts.timeslotId} value={ts.timeslotId}>{ts.name}</option>
              ))}
            </select>
            {moveTargets.length === 0 && (
              <p className="text-xs text-gray-400">{t('roster.action.move.empty')}</p>
            )}
          </div>
        )}

        {type === 'remove' && (
          <p className="text-sm text-gray-600">{t('roster.action.remove.description', { name: student.fullName })}</p>
        )}

        <div className="flex justify-end gap-2 mt-4">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
          >
            {t('button.cancel', { ns: 'common' })}
          </button>
          <button
            type="button"
            data-testid="roster-action-submit"
            disabled={pending || (type === 'move' && targetTimeslotId == null)}
            onClick={() => {
              if (type === 'move' && targetTimeslotId != null) moveMutation.mutate(targetTimeslotId)
              else if (type === 'remove') removeMutation.mutate()
            }}
            className="px-4 py-2 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
          >
            {t(`roster.action.${type}.submit`)}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
